import type { ConversationDetail } from '@chatbot/shared-types';
import { useMemo } from 'react';
import { conversationsApi } from '../../lib/conversations-api.ts';
import { toast } from '../Toast.tsx';

export interface ConversationActionHandlers {
  onSend: (text: string) => Promise<void>;
  onTake: () => Promise<void>;
  onRelease: () => Promise<void>;
  onResolve: () => Promise<void>;
  onReturnToBot: () => Promise<void>;
}

/**
 * Acciones de operador sobre la conversación seleccionada.
 * Cada acción refresca el hilo y la lista (la fila cambia de estado).
 */
export function useConversationActions(
  conv: ConversationDetail | null,
  refetchThread: () => Promise<void>,
  refetchList: () => Promise<void>,
): ConversationActionHandlers {
  return useMemo(() => {
    const noop = async () => {};
    if (!conv) {
      return {
        onSend: noop,
        onTake: noop,
        onRelease: noop,
        onResolve: noop,
        onReturnToBot: noop,
      };
    }

    const { tenantId, conversationId } = conv;
    const after = async () => {
      await refetchThread();
      await refetchList();
    };

    return {
      onSend: async (text: string) => {
        try {
          // El mensaje vuelve por WebSocket (message.sent); no lo appendeamos acá
          await conversationsApi.sendMessage(tenantId, conversationId, text);
        } catch (err) {
          toast.error(`No se pudo enviar: ${(err as Error).message}`);
        }
      },
      onTake: async () => {
        try {
          await conversationsApi.take(tenantId, conversationId);
          toast.success('Conversación tomada con éxito');
        } catch (err) {
          const msg = (err as Error).message;
          toast.error(
            msg.includes('already_taken') || msg.includes('409') ? 'Otro operador ya la tomó' : msg,
          );
        }
        await after();
      },
      onRelease: async () => {
        try {
          await conversationsApi.release(tenantId, conversationId);
          toast.info('Conversación devuelta al pool');
        } catch (err) {
          toast.error(`Error al soltar: ${(err as Error).message}`);
        }
        await after();
      },
      onResolve: async () => {
        try {
          await conversationsApi.resolve(tenantId, conversationId);
          toast.success('Conversación resuelta');
        } catch (err) {
          toast.error(`Error al resolver: ${(err as Error).message}`);
        }
        await after();
      },
      onReturnToBot: async () => {
        try {
          await conversationsApi.returnToBot(tenantId, conversationId);
          toast.success('Bot retomó el control');
        } catch (err) {
          toast.error(`Error al devolver al bot: ${(err as Error).message}`);
        }
        await after();
      },
    };
  }, [conv, refetchThread, refetchList]);
}
